import { useEffect, useRef, useState } from "react";
import { newTotpSecret } from "../crypto";
import { downloadCodes, TotpEnrol } from "../gate/Enrol";
import { passwordProblem } from "../gate/session";
import { errMsg } from "../lib/api";
import { fmtBytes } from "../lib/format";
import { generate } from "../lib/generator";
import { CAT_ICONS, PERM_DESC, PERM_LABEL, PERMS, REMIND_DAYS, TYPE_ORDER, TYPES, type ClientPerm } from "../lib/itemTypes";
import type { Field as FieldT, ItemType } from "../lib/types";
import { useVault, type ItemDraft } from "../state/store";
import { Button, Chip, Dialog, Field, IconButton, Input, Select, Switch, Tabs } from "../ui/ds";
import { Icon } from "../ui/Icon";
import { usePerspective, useUI, type Modal } from "./ui";

const MAX_FILE = 25 * 1024 * 1024;

export function Modals() {
  const { modal, setModal } = useUI();
  if (!modal) return null;
  const close = () => setModal(null);
  switch (modal.kind) {
    case "item":
      return <ItemDialog m={modal} close={close} />;
    case "share":
      return <ShareDialog m={modal} close={close} />;
    case "request":
      return <RequestDialog close={close} />;
    case "codes":
      return <CodesDialog close={close} />;
    case "client":
      return <ClientDialog close={close} />;
    case "cat":
      return <CategoryDialog close={close} />;
    case "delcat":
      return <DeleteCategoryDialog catId={modal.catId} close={close} />;
    case "password":
      return <PasswordDialog close={close} />;
    case "totp":
      return <TotpDialog close={close} />;
    case "export":
      return <ExportDialog close={close} />;
    case "confirm-delete":
      return <ConfirmDeleteDialog itemId={modal.itemId} close={close} />;
    case "invite-link":
      return <InviteLinkDialog m={modal} close={close} />;
  }
}

function useCurrentVault() {
  const p = usePerspective();
  const { nav } = useUI();
  return p.vaults.find((v) => v.id === nav.vaultId) ?? p.vaults[0] ?? null;
}

function Foot({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// New / edit item
// ---------------------------------------------------------------------------

function blankFields(type: ItemType): FieldT[] {
  return TYPES[type].f.map(([k, s, m]) => ({ k, v: "", ...(s ? { s: true } : {}), ...(m ? { m: true } : {}) }));
}

function ItemDialog({ m, close }: { m: Extract<Modal, { kind: "item" }>; close: () => void }) {
  const v = useVault();
  const p = usePerspective();
  const { go } = useUI();
  const existing = m.itemId ? v.data.items.find((i) => i.id === m.itemId) : undefined;
  const writable = p.vaults.filter((x) => p.canAdd(x));
  const startVault = existing?.vaultId ?? (m.vaultId && writable.some((x) => x.id === m.vaultId) ? m.vaultId : writable[0]?.id) ?? "";

  const [d, setD] = useState<ItemDraft | null>(() => {
    if (existing)
      return {
        id: existing.id,
        vaultId: existing.vaultId,
        type: existing.type,
        title: existing.title,
        catId: existing.catId ?? "",
        fields: existing.fields.map((f) => ({ ...f })),
        totp: existing.totp ?? "",
        expires: existing.expires ?? "",
        remind: existing.remind ?? 30,
        private: existing.private,
        file: null,
        existingFile: existing.file,
      };
    if (m.preset)
      return {
        id: null,
        vaultId: startVault,
        type: "login",
        title: "",
        catId: m.catId ?? "",
        fields: [{ k: "Username", v: "" }, { k: "Password", v: m.preset.password, s: true }, { k: "Website", v: "" }],
        totp: "",
        expires: "",
        remind: 30,
        private: false,
        file: null,
      };
    return null;
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const pickType = (type: ItemType) =>
    setD({ id: null, vaultId: startVault, type, title: "", catId: m.catId ?? "", fields: blankFields(type), totp: "", expires: "", remind: 30, private: false, file: null });

  if (!writable.length && !existing) {
    return (
      <Dialog title="You can’t add here" subtitle="Your access to this vault is view only." onClose={close} footer={<Button variant="ghost" onClick={close}>Close</Button>}>
        <div className="ds-dialog-body" />
      </Dialog>
    );
  }

  if (!d) {
    return (
      <Dialog title="What are you adding?" subtitle="Pick a type. You can change fields later." onClose={close}>
        <div className="type-grid">
          {TYPE_ORDER.map((t) => (
            <button key={t} type="button" className="type-tile" onClick={() => pickType(t)}>
              <Icon name={TYPES[t].icon} size={18} style={{ color: "var(--deep-teal)" }} />
              {TYPES[t].label}
            </button>
          ))}
        </div>
      </Dialog>
    );
  }

  const vault = v.data.vaults.find((x) => x.id === d.vaultId);
  const cats = v.data.categories.filter((c) => c.vaultId === d.vaultId);
  const set = (patch: Partial<ItemDraft>) => setD({ ...d, ...patch });
  const setField = (i: number, val: string) => set({ fields: d.fields.map((f, j) => (j === i ? { ...f, v: val } : f)) });
  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_FILE) return setErr("Files can be up to 25 MB.");
    setErr("");
    set({ file: f });
  };
  const fileLabel = d.file
    ? `${d.file.name} · ${fmtBytes(d.file.size)} · click to replace`
    : d.existingFile
      ? `${d.existingFile.name} · ${fmtBytes(d.existingFile.size)} · click to replace`
      : "Drop a file or click to choose · up to 25 MB";

  const save = async () => {
    if (!d.title.trim()) return setErr("Give it a title.");
    if (v.guard()) return;
    setBusy(true);
    setErr("");
    try {
      const id = await v.saveItem(d);
      close();
      go({ view: "vault", vaultId: d.vaultId, tab: "items", itemId: id, pane: "detail", q: "", cat: d.catId || "all" });
      v.toast(d.id ? "Changes saved" : `Saved to ${vault?.name}`, p.asClient && !d.id ? "Agile Software can see it now." : undefined);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={d.id ? `Edit ${existing?.title}` : `New ${TYPES[d.type].label.toLowerCase()}`}
      subtitle="Encrypted on this device before it’s saved."
      onClose={close}
      footer={
        <Foot>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void save()} busy={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </Foot>
      }
    >
      <form
        className="ds-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Title" error={err}>
          <Input value={d.title} placeholder="e.g. Shopify admin" onChange={(x) => { set({ title: x }); setErr(""); }} />
        </Field>
        {!p.asClient && writable.length > 1 ? (
          <Field label="Vault">
            <Select
              label="Vault"
              options={writable.map((x) => ({ value: x.id, label: x.name }))}
              value={d.vaultId}
              onChange={(x) => set({ vaultId: x, catId: "" })}
            />
          </Field>
        ) : null}
        <Field label="Category">
          <Select label="Category" options={[{ value: "", label: "No category" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]} value={d.catId} onChange={(x) => set({ catId: x })} />
        </Field>
        <div className="two-col">
          <Field label="Expiry date" hint="Optional. Leave blank if it doesn't expire.">
            <Input type="date" value={d.expires} onChange={(x) => set({ expires: x })} />
          </Field>
          <Field label="Warn me">
            <Select
              label="Warn me"
              options={REMIND_DAYS.map((n) => ({ value: String(n), label: `${n} days before` }))}
              value={String(d.remind)}
              onChange={(x) => set({ remind: parseInt(x, 10) })}
            />
          </Field>
        </div>
        {vault?.kind === "client" && !p.asClient ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid var(--border-default)", borderRadius: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-navy)" }}>Share with {vault.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{d.private ? "Only you can see this item." : "Everyone with access to this vault can see it."}</div>
            </div>
            <Switch checked={!d.private} ariaLabel={`Share with ${vault.name}`} onChange={(on) => set({ private: !on })} />
          </div>
        ) : null}
        {d.fields.map((f, i) => (
          <Field key={i} label={f.k}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Input
                  multiline={f.m}
                  rows={6}
                  mono={f.s}
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={f.v}
                  aria-label={f.k}
                  placeholder={f.m && d.type === "env" ? "KEY=value, one per line" : ""}
                  onChange={(x) => setField(i, x)}
                />
              </div>
              {f.s && !f.m ? (
                <IconButton icon="dices" label="Generate" onClick={() => setField(i, generate({ mode: "pw", len: 20, upper: true, num: true, sym: true, words: 5 }))} />
              ) : null}
            </div>
          </Field>
        ))}
        {d.type === "login" ? (
          <Field label="2FA setup key" hint="Optional. Paste the key or otpauth:// link to show live codes on this login.">
            <Input value={d.totp} mono autoComplete="off" onChange={(x) => set({ totp: x })} />
          </Field>
        ) : null}
        {d.type === "file" ? (
          <>
            <button
              type="button"
              className={`drop-zone${over ? " is-over" : ""}`}
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                pickFile(e.dataTransfer.files[0]);
              }}
            >
              <Icon name="paperclip" size={18} />
              <span className="mono">{fileLabel}</span>
            </button>
            <input ref={fileInput} type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          </>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Share: invite to vault / one-time link
// ---------------------------------------------------------------------------

function ShareDialog({ m, close }: { m: Extract<Modal, { kind: "share" }>; close: () => void }) {
  const v = useVault();
  const vault = useCurrentVault();
  const { setModal } = useUI();
  const isClient = vault?.kind === "client";
  const [tab, setTab] = useState<"invite" | "link">(isClient ? m.tab : "link");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [perm, setPerm] = useState<ClientPerm>("view");
  const [exp, setExp] = useState(v.session.settings.linkExpiry);
  const [views, setViews] = useState("1 view");
  const [usePass, setUsePass] = useState(true);
  const [created, setCreated] = useState<{ url: string; passphrase: string | null } | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const item = m.itemId ? v.data.items.find((i) => i.id === m.itemId) : undefined;
  if (!vault) return null;

  const primary = async () => {
    if (v.guard()) return;
    setErr("");
    if (tab === "invite") {
      const addr = email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) return setErr("Enter a valid email address.");
      setBusy(true);
      try {
        const res = await v.invite(vault.id, addr, name.trim(), perm);
        if (res.inviteUrl) setModal({ kind: "invite-link", email: addr, url: res.inviteUrl, vaultName: vault.name });
        else {
          close();
          v.toast(`${addr} added`, "They already have an account. They get access next time you both sign in.");
        }
      } catch (e) {
        setErr(errMsg(e));
      } finally {
        setBusy(false);
      }
    } else if (item) {
      setBusy(true);
      try {
        setCreated(await v.createLink(item, { exp, views: views === "1 view" ? 1 : 5, usePass }));
      } catch (e) {
        setErr(errMsg(e));
      } finally {
        setBusy(false);
      }
    }
  };

  const title = tab === "invite" ? `Invite to ${vault.name}` : "Create a one-time link";
  const sub = tab === "invite" ? "They get their own sign-in to this vault and nothing else." : "Anyone with the link can open this item until it expires or runs out of views.";

  return (
    <Dialog
      title={title}
      subtitle={sub}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {created ? "Done" : "Cancel"}
          </Button>
          {!created && (tab === "invite" || item) ? (
            <Button arrow busy={busy} onClick={() => void primary()}>
              {tab === "invite" ? "Send invite" : "Create link"}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="ds-dialog-body" style={{ gap: 16 }}>
        {isClient && !created ? (
          <Tabs
            variant="pill"
            items={[
              { value: "invite", label: "Invite to vault" },
              { value: "link", label: "One-time link" },
            ]}
            value={tab}
            onChange={(t) => {
              setTab(t);
              setErr("");
            }}
          />
        ) : null}
        {tab === "invite" ? (
          <form
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
            onSubmit={(e) => {
              e.preventDefault();
              void primary();
            }}
          >
            <Field label="Client email" error={err}>
              <Input type="email" icon="mail" placeholder="name@company.co.uk" value={email} onChange={(x) => { setEmail(x); setErr(""); }} />
            </Field>
            <Field label="Their name" hint="Optional. Shown in Access and Activity.">
              <Input value={name} onChange={setName} />
            </Field>
            <div className="label-sm">Permission</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} role="radiogroup" aria-label="Permission">
              {PERMS.map((x) => (
                <button key={x} type="button" role="radio" aria-checked={perm === x} className={`radio-card${perm === x ? " is-on" : ""}`} onClick={() => setPerm(x)}>
                  <span className="radio-dot" />
                  <span>
                    <span style={{ display: "block", fontWeight: 600, color: "var(--ink-navy)", fontSize: 14 }}>{PERM_LABEL[x]}</span>
                    <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{PERM_DESC[x]}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="mist-box notice">
              <Icon name="shield-check" size={16} style={{ color: "var(--deep-teal)", marginTop: 3 }} />
              <span>They set up 2FA and save backup codes before they can open anything.</span>
            </div>
            <button type="submit" hidden />
          </form>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: "1px solid var(--border-default)", borderRadius: 8 }}>
              <Icon name={item ? TYPES[item.type].icon : "file"} size={16} style={{ color: "var(--deep-teal)" }} />
              <span style={{ fontWeight: 600, color: "var(--ink-navy)" }}>{item ? item.title : "Open an item first to share it by link"}</span>
            </div>
            {!created && item ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div className="label-sm">Expires after</div>
                  <Tabs variant="pill" items={["1 hour", "24 hours", "7 days"]} value={exp} onChange={setExp} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div className="label-sm">Can be opened</div>
                  <Tabs variant="pill" items={["1 view", "5 views"]} value={views} onChange={setViews} />
                </div>
                <Switch label="Require a passphrase to open" checked={usePass} onChange={setUsePass} />
                {item.file ? <div className="muted" style={{ fontSize: 12 }}>The attachment isn’t included in links, only the fields.</div> : null}
                {err ? <div className="ds-field-error">{err}</div> : null}
              </>
            ) : null}
            {created ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="copy-box">
                  <span className="copy-box-value">{created.url}</span>
                  <IconButton icon="copy" label="Copy link" variant="ghost" size="sm" onClick={() => void v.copy(created.url, "Link")} />
                </div>
                {created.passphrase ? (
                  <>
                    <div className="copy-box">
                      <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Passphrase</span>
                      <span className="copy-box-value">{created.passphrase}</span>
                      <IconButton icon="copy" label="Copy passphrase" variant="ghost" size="sm" onClick={() => void v.copy(created.passphrase!, "Passphrase")} />
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                      Send the passphrase a different way — by phone or text, not in the same email as the link.
                    </div>
                  </>
                ) : null}
                <div className="mono" style={{ fontSize: 12, color: "var(--deep-teal)" }}>
                  ✓ Expires in {exp} · {views} · logged in Activity
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function InviteLinkDialog({ m, close }: { m: Extract<Modal, { kind: "invite-link" }>; close: () => void }) {
  const v = useVault();
  return (
    <Dialog
      title={`Invite ready for ${m.email}`}
      subtitle="Agile Vault doesn’t send email yet, so send them this link yourself. It works once and expires in 14 days."
      onClose={close}
      footer={
        <Button onClick={close} arrow>
          Done
        </Button>
      }
    >
      <div className="ds-dialog-body">
        <div className="copy-box">
          <span className="copy-box-value">{m.url}</span>
          <IconButton icon="copy" label="Copy invite link" variant="ghost" size="sm" onClick={() => void v.copy(m.url, "Invite link")} />
        </div>
        <div className="mist-box notice">
          <Icon name="shield-check" size={16} style={{ color: "var(--deep-teal)", marginTop: 3 }} />
          <span>
            They choose a master password, set up 2FA and save backup codes. Once they have, your app shares {m.vaultName} with them the next time you open it.
          </span>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Request a credential
// ---------------------------------------------------------------------------

function RequestDialog({ close }: { close: () => void }) {
  const v = useVault();
  const vault = useCurrentVault();
  const { go } = useUI();
  const members = v.data.members.filter((x) => x.vaultId === vault?.id);
  const [to, setTo] = useState(members[0]?.userId ?? "");
  const [type, setType] = useState<ItemType>("login");
  const [title, setTitle] = useState("");
  const [fields, setFields] = useState<string[]>(["Username", "Password"]);
  const [msg, setMsg] = useState("");
  const [exp, setExp] = useState("7 days");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!vault) return null;

  const send = async () => {
    if (v.guard()) return;
    const member = members.find((x) => x.userId === to);
    if (!member) return setErr("Invite someone to this vault first.");
    if (!title.trim()) return setErr("Say what you need.");
    if (!fields.length) return setErr("Pick at least one field.");
    setBusy(true);
    try {
      await v.createRequest(vault.id, { toUserId: member.userId, toName: member.name, title: title.trim(), type, fields, msg: msg.trim(), exp });
      close();
      go({ tab: "requests" });
      v.toast(`Request sent to ${member.name}`, "It lands in this vault when they fill it in. Let them know it’s waiting.");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Request a credential"
      subtitle={`They get a secure form. What they send goes straight into ${vault.name}.`}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button arrow busy={busy} onClick={() => void send()}>
            Send request
          </Button>
        </>
      }
    >
      <div className="ds-dialog-body">
        {!members.length ? (
          <div className="banner banner-warning">
            {v.data.invites.some((i) => i.vaultId === vault.id)
              ? "Nobody has finished joining this vault yet. Requests open up once they have."
              : "Nobody has access to this vault yet. Invite someone first."}
          </div>
        ) : null}
        <Field label="Ask">
          <Select label="Ask" options={members.length ? members.map((x) => ({ value: x.userId, label: x.name })) : [{ value: "", label: "—" }]} value={to} onChange={setTo} />
        </Field>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="label-sm">Type</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TYPE_ORDER.filter((t) => t !== "file").map((t) => (
              <Chip
                key={t}
                label={TYPES[t].label}
                on={type === t}
                onClick={() => {
                  setType(t);
                  setFields(TYPES[t].f.map((x) => x[0]));
                }}
              />
            ))}
          </div>
        </div>
        <Field label="What do you need?" error={err}>
          <Input placeholder="e.g. Royal Mail Click & Drop login" value={title} onChange={(x) => { setTitle(x); setErr(""); }} />
        </Field>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="label-sm">Fields they fill in</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TYPES[type].f.map(([k]) => (
              <Chip
                key={k}
                label={k}
                on={fields.includes(k)}
                onClick={() => setFields((f) => (f.includes(k) ? f.filter((z) => z !== k) : TYPES[type].f.map((x) => x[0]).filter((z) => z === k || f.includes(z))))}
              />
            ))}
          </div>
        </div>
        <Field label="Message">
          <Input multiline rows={3} placeholder="What it's for, so they know why you're asking" value={msg} onChange={setMsg} />
        </Field>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="label-sm">Request expires after</div>
          <Tabs variant="pill" items={["24 hours", "7 days", "30 days"]} value={exp} onChange={setExp} />
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Backup codes
// ---------------------------------------------------------------------------

function CodesDialog({ close }: { close: () => void }) {
  const v = useVault();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [used, setUsed] = useState(v.session.backup.used);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    v.backupCodes().then(setCodes, () => setCodes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const regen = async () => {
    setBusy(true);
    try {
      setCodes(await v.regenerateBackupCodes());
      setUsed([]);
      v.toast("New backup codes generated", "Your old codes no longer work.");
    } catch (e) {
      v.toast("Couldn't regenerate", errMsg(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Backup codes"
      subtitle="Use one if you lose your authenticator."
      width={440}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" icon="refresh-cw" busy={busy} onClick={() => void regen()}>
            Regenerate
          </Button>
          <Button variant="secondary" icon="copy" onClick={() => codes && void v.copy(codes.join("\n"), "Backup codes")}>
            Copy
          </Button>
          <Button icon="download" onClick={() => codes && downloadCodes(codes, v.me.email, v.toast)}>
            Download
          </Button>
        </>
      }
    >
      <div className="ds-dialog-body">
        {codes ? (
          <div className="codes-grid">
            {codes.map((c, i) => (
              <div key={c + i} className={used.includes(i) ? "is-used" : ""}>
                {c}
              </div>
            ))}
          </div>
        ) : (
          <div className="spinner" />
        )}
        <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
          Each code works once. Struck-through codes are used. Keep them somewhere other than this vault.
        </div>
        {!v.session.backup.saved ? (
          <Button
            variant="secondary"
            size="sm"
            icon="check"
            onClick={() => void v.markBackupSaved().then(() => v.toast("Thanks", "Your codes are marked as saved."), (e) => v.toast("Couldn't record that", errMsg(e), "danger"))}
          >
            I’ve saved these somewhere safe
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Vaults & categories
// ---------------------------------------------------------------------------

function ClientDialog({ close }: { close: () => void }) {
  const v = useVault();
  const { openVault, setModal } = useUI();
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (v.guard()) return;
    if (!name.trim()) return setErr("Name the client.");
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Enter a valid email address.");
    setBusy(true);
    try {
      const res = await v.createVault(name.trim(), contact.trim(), email.trim().toLowerCase());
      openVault(res.id);
      if (res.inviteUrl) setModal({ kind: "invite-link", email: email.trim().toLowerCase(), url: res.inviteUrl, vaultName: name.trim() });
      else {
        close();
        v.toast(`${name.trim()} vault created`, res.error ?? (res.added ? `${email.trim()} added.` : "Invite someone from the Access tab when you’re ready."));
      }
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="New client vault"
      subtitle="One vault per client. Nothing in it is shared until you invite someone."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button arrow busy={busy} onClick={() => void create()}>
            Create vault
          </Button>
        </>
      }
    >
      <form
        className="ds-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <Field label="Client name" error={err}>
          <Input placeholder="e.g. Fenwick Studio" value={name} onChange={(x) => { setName(x); setErr(""); }} />
        </Field>
        <Field label="Contact name" hint="Optional. You can invite them now or later.">
          <Input value={contact} onChange={setContact} />
        </Field>
        <Field label="Contact email">
          <Input type="email" icon="mail" value={email} onChange={setEmail} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function CategoryDialog({ close }: { close: () => void }) {
  const v = useVault();
  const vault = useCurrentVault();
  const { go } = useUI();
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("folder");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!vault) return null;
  const cats = v.data.categories.filter((c) => c.vaultId === vault.id);
  const create = async () => {
    if (v.guard()) return;
    const n = name.trim();
    if (!n) return setErr("Name the category.");
    if (cats.some((c) => c.name.toLowerCase() === n.toLowerCase())) return setErr(`There’s already a category called ${n}.`);
    setBusy(true);
    try {
      const id = await v.createCategory(vault.id, n, icon);
      close();
      go({ cat: id, itemId: null, pane: "list" });
      v.toast(`${n} created`, "Add items to it from New item, or edit an existing one.");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`New category in ${vault.name}`}
      subtitle="Group related credentials, like everything for one system."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button arrow busy={busy} onClick={() => void create()}>
            Create category
          </Button>
        </>
      }
    >
      <form
        className="ds-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <Field label="Name" error={err}>
          <Input placeholder="e.g. Microsoft 365" value={name} onChange={(x) => { setName(x); setErr(""); }} />
        </Field>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="label-sm">Icon</div>
          <div className="icon-grid">
            {CAT_ICONS.map((ic) => (
              <button key={ic} type="button" aria-label={ic} aria-pressed={icon === ic} className={icon === ic ? "is-on" : ""} onClick={() => setIcon(ic)}>
                <Icon name={ic} size={18} />
              </button>
            ))}
          </div>
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function DeleteCategoryDialog({ catId, close }: { catId: string; close: () => void }) {
  const v = useVault();
  const vault = useCurrentVault();
  const { go } = useUI();
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const cat = v.data.categories.find((c) => c.id === catId);
  if (!vault || !cat) return null;
  const others = v.data.categories.filter((c) => c.vaultId === vault.id && c.id !== catId);
  const count = v.data.items.filter((i) => i.catId === catId).length;
  const toName = to ? (others.find((c) => c.id === to)?.name ?? "") : "Uncategorised";
  const confirm = async () => {
    if (v.guard()) return;
    setBusy(true);
    try {
      await v.deleteCategory(vault.id, cat, to, count, toName);
      close();
      go({ cat: to || null, itemId: null, pane: "list" });
      v.toast(`${cat.name} deleted`, count ? `${count} ${count === 1 ? "item" : "items"} moved to ${toName}.` : undefined);
    } catch (e) {
      v.toast("Couldn't delete it", errMsg(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`Delete ${cat.name}?`}
      subtitle={`${count} ${count === 1 ? "item is" : "items are"} in this category.`}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button icon="trash-2" busy={busy} onClick={() => void confirm()}>
            Delete category
          </Button>
        </>
      }
    >
      <div className="ds-dialog-body">
        <Field label="Move its items to" hint="Items aren't deleted. Archived items move too.">
          <Select label="Move its items to" options={[{ value: "", label: "Uncategorised" }, ...others.map((c) => ({ value: c.id, label: c.name }))]} value={to} onChange={setTo} />
        </Field>
      </div>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ itemId, close }: { itemId: string; close: () => void }) {
  const v = useVault();
  const { go } = useUI();
  const [busy, setBusy] = useState(false);
  const item = v.data.items.find((i) => i.id === itemId);
  if (!item) return null;
  return (
    <Dialog
      title={`Delete ${item.title}?`}
      subtitle="This can’t be undone. Any one-time links to it stop working. Archive it instead if you might need it again."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            icon="trash-2"
            busy={busy}
            onClick={() => {
              setBusy(true);
              void v
                .deleteItem(item)
                .then(() => {
                  close();
                  go({ itemId: null, pane: "list" });
                })
                .catch((e) => v.toast("Couldn't delete it", errMsg(e), "danger"))
                .finally(() => setBusy(false));
            }}
          >
            Delete
          </Button>
        </>
      }
    >
      <div />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Account: password, 2FA, export
// ---------------------------------------------------------------------------

function PasswordDialog({ close }: { close: () => void }) {
  const v = useVault();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!cur) return setErr("Enter your current master password.");
    const problem = passwordProblem(pw, pw2);
    if (problem) return setErr(problem);
    setBusy(true);
    setErr("");
    try {
      await v.changePassword(cur, pw);
      close();
      v.toast("Master password changed", "Use the new one next time you unlock. Other sessions were signed out.");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Change master password"
      subtitle="Your vault isn’t re-encrypted, only re-locked with the new password. Nobody can recover it if you forget it."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button busy={busy} onClick={() => void save()}>
            {busy ? "Re-locking…" : "Change password"}
          </Button>
        </>
      }
    >
      <form
        className="ds-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Current master password">
          <Input type="password" autoComplete="current-password" value={cur} onChange={(x) => { setCur(x); setErr(""); }} />
        </Field>
        <Field label="New master password" hint="At least 12 characters.">
          <Input type="password" autoComplete="new-password" value={pw} onChange={(x) => { setPw(x); setErr(""); }} />
        </Field>
        <Field label="Confirm new master password" error={err}>
          <Input type="password" autoComplete="new-password" value={pw2} onChange={(x) => { setPw2(x); setErr(""); }} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function TotpDialog({ close }: { close: () => void }) {
  const v = useVault();
  const [secret] = useState(newTotpSecret);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (code: string) => {
    if (code.replace(/\D/g, "").length !== 6) return setErr("The code is 6 digits. Check your authenticator app.");
    setBusy(true);
    setErr("");
    try {
      await v.reconfigureTotp(secret, code);
      close();
      v.toast("Two-step verification updated", "Your old authenticator entry no longer works. Delete it from the app.");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title="Reconfigure two-step verification" subtitle="Add this new key to your authenticator. Your old one stops working once you confirm." onClose={close}>
      <div className="ds-dialog-body">
        <TotpEnrol secret={secret} account={v.me.email} busy={busy} error={err} submitLabel="Confirm" onSubmit={(c) => void submit(c)} />
      </div>
    </Dialog>
  );
}

function ExportDialog({ close }: { close: () => void }) {
  const v = useVault();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!pw) return setErr("Enter your master password.");
    setBusy(true);
    setErr("");
    try {
      const name = await v.exportBackup(pw);
      close();
      v.toast("Export ready", `${name} · locked with your master password.`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Export an encrypted backup"
      subtitle="Every vault you can open, in one file. It’s encrypted with your master password, so keep both safe."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button icon="download" busy={busy} onClick={() => void go()}>
            Export
          </Button>
        </>
      }
    >
      <form
        className="ds-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          void go();
        }}
      >
        <Field label="Master password" error={err}>
          <Input type="password" autoComplete="current-password" value={pw} onChange={(x) => { setPw(x); setErr(""); }} />
        </Field>
        <BackupNote />
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function BackupNote() {
  return (
    <div className="mist-box notice">
      <Icon name="info" size={16} style={{ color: "var(--deep-teal)", marginTop: 3 }} />
      <span>Attachments aren’t included. The README explains how to decrypt the file without Agile Vault.</span>
    </div>
  );
}

